import { type AxiosResponse, type AxiosInstance } from "axios";
import { ICacheService } from './ICacheService';

export class AxiosHttpService implements IHttpService {
    private instance: AxiosInstance;
    private cacheService?: ICacheService;
    private readonly cacheTTLSeconds = 10;

    private async invalidateCacheAfterMutation(): Promise<void> {
        if (!this.cacheService) {
            return;
        }

        try {
            await this.cacheService.clear();
        } catch {
            // No bloquear mutaciones por fallas de invalidacion de cache
        }
    }

    constructor(axiosInstance: AxiosInstance, cache?: ICacheService) {
        this.instance = axiosInstance;
        this.cacheService = cache;
    }

    getCache(): ICacheService | undefined {
        return this.cacheService;
    }

    async get<T>(url: string, options?: HttpGetOptions): Promise<T | HttpResponse<T>> {
        const { params = {}, headers, includeHeaders = false } = options || {};
        const useCache = !!this.cacheService;
        type CacheEntry<V> = { data: V; etag?: string; timestamp: number };
        const buildCacheKey = (): string => {
            const search = new URLSearchParams();
            Object.keys(params || {}).sort().forEach((k) => {
                const v = (params as any)[k];
                if (v !== undefined && v !== null) search.append(k, String(v));
            });
            const qs = search.toString();
            return qs ? `${url}?${qs}` : url;
        };

        const cacheKey = buildCacheKey();
        const now = Date.now();
        const ttlMs = this.cacheTTLSeconds * 1000;
        let cached: CacheEntry<T> | null = null;

        if (useCache) {
            try {
                cached = await this.cacheService!.get<CacheEntry<T>>(cacheKey);
                if (cached && (now - cached.timestamp) < ttlMs) {
                    if (includeHeaders) {
                        return { data: cached.data as T, headers: cached.etag ? { etag: cached.etag } : undefined };
                    }
                    return cached.data as T;
                }
            } catch (e) {
                // fallo de cache no bloquea la solicitud de red
            }
        }

        let mergedHeaders: Record<string, string> | undefined = headers ? { ...headers } : undefined;
        if (useCache && cached?.etag) {
            if (mergedHeaders) {
                mergedHeaders['If-None-Match'] = cached.etag;
            } else {
                mergedHeaders = { 'If-None-Match': cached.etag };
            }
        }

        try {
            const response: AxiosResponse<T> = await this.instance.get(url, { params, headers: mergedHeaders });
            if (useCache) {
                const etagHeader = (response.headers as any)?.etag as string | undefined;
                const entry: CacheEntry<T> = { data: response.data, etag: etagHeader, timestamp: Date.now() };
                try { await this.cacheService!.set(cacheKey, entry); } catch {}
            }
            if (includeHeaders) {
                return {
                    data: response.data,
                    headers: response.headers as Record<string, string>
                };
            }
            return response.data;
        } catch (error: any) {
            if (error?.response?.status === 304 && useCache && cached) {
                // actualizar timestamp para extender vigencia y devolver cache
                const refreshed: CacheEntry<T> = { data: cached.data, etag: cached.etag, timestamp: Date.now() };
                try { await this.cacheService!.set(cacheKey, refreshed); } catch {}
                if (includeHeaders) {
                    return { data: cached.data as T, headers: cached.etag ? { etag: cached.etag } : undefined };
                }
                return cached.data as T;
            }
            // fallback a cache si hay, de lo contrario propagar error
            if (useCache && cached) {
                if (includeHeaders) {
                    return { data: cached.data as T, headers: cached.etag ? { etag: cached.etag } : undefined };
                }
                return cached.data as T;
            }
            console.error(`Error al hacer GET a ${url}:`, error);
            throw error;
        }
    }

    async post<T, U>(url: string, options?: HttpPostOptions<U>): Promise<T> {
        const { data, headers } = options || {};
        try {
            const response: AxiosResponse<T> = await this.instance.post(url, data, { headers });
            await this.invalidateCacheAfterMutation();
            return response.data;
        } catch (error: any) {
            console.error(`Error al hacer POST a ${url}:`, error);
            throw error;
        }
    }

    async delete<T>(url: string, options?: HttpDeleteOptions): Promise<T> {
        const { headers } = options || {};
        try {
            const response: AxiosResponse<T> = await this.instance.delete(url, { headers });
            await this.invalidateCacheAfterMutation();
            return response.data;
        } catch (error: any) {
            console.error(`Error al hacer DELETE a ${url}:`, error);
            throw error;
        }
    }

    async patch<T, U>(url: string, options?: HttpPatchOptions<U>): Promise<T> {
        const { data, headers } = options || {};
        try {
            const response: AxiosResponse<T> = await this.instance.patch(url, data, { headers });
            await this.invalidateCacheAfterMutation();
            return response.data;
        } catch (error: any) {
            console.error(`Error al hacer PATCH a ${url}:`, error);
            throw error;
        }
    }

    async put<T, U>(url: string, options?: HttpPutOptions<U>): Promise<T> {
        const { data, headers } = options || {};
        try {
            const response: AxiosResponse<T> = await this.instance.put(url, data, { headers });
            await this.invalidateCacheAfterMutation();
            return response.data;
        } catch (error: any) {
            console.error(`Error al hacer PUT a ${url}:`, error);
            throw error;
        }
    }

}
